import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EnvironmentService } from '@services/environment.service';
import { TranslationService } from '@services/translation.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, Subject, Subscription } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { WebSocketService } from '../../services/web-socket.service';

@Component({
  selector: 'app-course-imports',
  templateUrl: './course-imports.component.html',
  styleUrl: './course-imports.component.scss',
  standalone: false,
})
export class CourseImportsComponent implements OnInit, OnDestroy {
  constructor(
    private environmentService: EnvironmentService,
    private http: HttpClient,
    private translationService: TranslationService,
    private fb: FormBuilder,
    private socket: WebSocketService
  ) {}

  @Input() filterType: string | undefined;
  private _customColumns: any[] = [];
  @Input()
  get customColumns(): any[] {
    return this._customColumns;
  }
  set customColumns(value: any[]) {
    this._customColumns = value;
    this.columns = [...this.baseColumns, ...this._customColumns];
  }
  private _refreshTrigger: number = 0;
  @Input()
  get refreshTrigger(): number {
    return this._refreshTrigger;
  }
  set refreshTrigger(value: number) {
    this._refreshTrigger = value;
    // ignore if initial load, incase component doesn't use refreshTrigger, we load it in ngOnInit
    if (this.initialLoad === false) {
      this.pageNumber = 1;
      this.initSearch();
    }
  }
  public initialLoad = true;
  public isLoading: boolean = true;
  public sortBy = '';
  public sortAsc = false;
  public itemCount;
  public pageNumber = 1;
  public perPageOptions = [{ count: 10 }, { count: 25 }, { count: 50 }];
  public perPage = 10;
  public queryParams;
  public rows: any[] = [];
  public filterInput = '';
  public columns: any[] = [];
  public baseColumns: any[] = [
    {
      property: 'course_code',
      text: this.translationService.getTranslatedContent(
        'course_code',
        'Course Code'
      ),
      isSortable: true,
      width: '150px',
    },
    {
      property: 'title',
      text: this.translationService.getTranslatedContent(
        'course_title',
        'Course Title'
      ),
      isSortable: true,
      width: '150px',
      type: 'link',
    },

    {
      property: 'lms_id',
      text: 'LMS ID',
      width: '150px',
      isSortable: true,
    },
    {
      property: 'imported_at',
      text: this.translationService.getTranslatedContent(
        'import_attempt',
        'Import Attempt'
      ),
      type: 'fulldate',
      width: '150px',
      isSortable: true,
    },
    {
      property: 'status',
      text: this.translationService.getTranslatedContent('status', 'Status'),
      isSortable: true,
      width: '150px',
      type: 'link',
    },
  ];
  public statusOptions = [
    {
      text: this.translationService.getTranslatedContent('all', 'All'),
      value: '',
    },
    {
      text: this.translationService.getTranslatedContent(
        'complete',
        'Complete'
      ),
      value: 'complete',
    },
    {
      text: this.translationService.getTranslatedContent('queued', 'Queued'),
      value: 'queued',
    },
    {
      text: this.translationService.getTranslatedContent('failed', 'Failed'),
      value: 'failed',
    },
    {
      text: this.translationService.getTranslatedContent(
        'processing',
        'Processing'
      ),
      value: 'processing',
    },
    {
      text: this.translationService.getTranslatedContent(
        'not_found',
        'Not Found'
      ),
      value: 'not_found',
    },
    {
      text: this.translationService.getTranslatedContent('skipped', 'Skipped'),
      value: 'skipped',
    },
  ];
  public statusControl = this.fb.control('');
  public statusControl$;
  public destroy$: Subject<boolean> = new Subject<boolean>();
  private importSubscription$: Subscription;
  ngOnInit(): void {
    this.columns = [...this.baseColumns, ...this._customColumns];
    this.initSearch();
    this.statusControl$ = this.statusControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageNumber = 1;
        this.initSearch();
      });
    this.socket
      .subscribeToMessages('import-courses')
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        const toUpdate = this.rows.findIndex(
          item => Number(item.import_id) === Number(message.data.id)
        );
        if (toUpdate !== -1) {
          // keep incase we need to update the row
          /*const updated = { ...this.rows[toUpdate] };
          updated.status = message.data.status;
          this.rows[toUpdate] = updated;
          console.log('updated', this.rows[toUpdate]);*/
          this.initSearch();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.importSubscription$?.unsubscribe) {
      this.importSubscription$.unsubscribe();
    }
    this.statusControl$.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getImportList() {
    if (this.importSubscription$?.unsubscribe) {
      this.importSubscription$.unsubscribe();
    }
    this.isLoading = true;
    this.initialLoad = false;
    let url = `${this.environmentService.environment.apiRoot}/import-sets/courses`;
    if (this.queryParams) url += `?${this.queryParams}`;
    this.importSubscription$ = this.http
      .request('get', url)
      .pipe(catchError(error => of({ error })))
      .subscribe((response: any) => {
        this.rows = this.mapToRows(response);
        this.itemCount = response?.totalItems || 0;
        this.isLoading = false;
      });
    return this.importSubscription$;
  }

  handleSearch($event) {
    this.filterInput = $event;
    this.pageNumber = 1;
    this.initSearch();
  }

  initSearch() {
    const queryParams = this.getQueryParams();
    this.queryParams = queryParams;
    this.getImportList();
  }

  mapToRows(response) {
    if (!Array.isArray(response?.listItems)) return [];
    return response.listItems.map(this.importMapFunction.bind(this));
  }

  importMapFunction(course) {
    let title: any;
    switch (true) {
      case course.status === 'complete' && !!course.id:
        title = {
          text: course?.title || course.course_code || course.id,
          link: `courses/${course.id}`,
        };
        break;
      case course.status === 'failed' && !!course.id && !!course.reimport_set:
        title = {
          text:
            'Successful Course - ' +
              (course?.title || course.course_code || course.id) || '',
          link: `courses/${course.id}`,
        };
        break;
      case course.status === 'skipped' && !!course.id:
        title = {
          text:
            'Original Course - ' +
            (course.title || course.course_code || course.id),
          link: `courses/${course.id}`,
        };
        break;
      default:
        title = { text: course?.title || course.course_code || course.id };
    }
    let status;
    if (course.status === 'failed') {
      if (course.reimport_set) {
        status = {
          text: this.translationService.getTranslatedContent(
            'failed_reimported',
            'Failed Reimported'
          ),
          link: `imports/import/${course.reimport_set}`,
        };
      } else {
        status = {
          text: this.translationService.getTranslatedContent(
            'failed-notified',
            'Failed, K16 Notified'
          ),
        };
      }
    } else {
      status = {
        text: this.translationService.getTranslatedContent(
          course.status,
          course.status
        ),
      };
    }
    return {
      ...course,
      original_canvas_id: course?.original_canvas_id || '',
      course_code: course?.course_code || '',
      imported_at: course?.imported_at,
      title,
      status,
      srcfolder: course?.srcfolder?.split(/\//g)?.pop(),
    };
  }

  getQueryParams() {
    const queryParams: any = [];
    queryParams.push(`direction=${this.sortAsc ? 'ASC' : 'DESC'}`);
    if (this.filterType) queryParams.push(`type=${this.filterType}`);
    if (this.filterInput)
      queryParams.push(`term=${encodeURIComponent(this.filterInput)}`);
    if (this.statusControl.value)
      queryParams.push(`status=${this.statusControl.value}`);
    if (this.sortBy) queryParams.push(`orderBy=${this.sortBy}`);
    if (this.pageNumber) queryParams.push(`pageNumber=${this.pageNumber - 1}`);
    if (this.perPage) queryParams.push(`perPage=${this.perPage}`);
    return queryParams.filter(t => !!t).join('&');
  }

  sortChange(header) {
    if (!header.property) return;
    this.sortAsc = header?.property === this.sortBy ? !this.sortAsc : true;
    this.sortBy = header?.property;
    this.pageNumber = 1;
    this.initSearch();
  }

  setPageCount(count) {
    this.perPage = count;
    this.pageNumber = 1;
    this.initSearch();
  }

  activePageChange(page) {
    this.pageNumber = page;
    this.initSearch();
  }
}
